import { useState, useEffect, useMemo } from "react";
import { useFeedback } from "../components/ActionFeedback";
import { motion, AnimatePresence } from "framer-motion";
import API from "../api/axios";
import Lottie from "lottie-react";
import noDataAnimation from "../layout/no_data.json";
import { QRCodeSVG } from "qrcode.react";

/* ── Animation variants ─────────────────────────────────── */
const container = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};
const item = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};
const row = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.35 } },
};

/* ── Status config ─────────────────────────────────────── */
const statusCfg = {
  Selling: { label: "Selling",  bg: "bg-amber-400",   text: "text-[#0a0c10]" },
  Closing: { label: "Closing",  bg: "bg-red-500",     text: "text-white"     },
  Draft:   { label: "Draft",    bg: "bg-slate-100",   text: "text-slate-500" },
};

/* ── Shared input class ────────────────────────────────── */
const inputCls =
  "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0a0c10]/10 transition-all";

export default function Events() {
  const { showToast, showModal, closeModal } = useFeedback();
  const [events, setEvents]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState("");
  const [filter, setFilter]     = useState("all");

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const res = await API.get("/events");
      setEvents(res.data);
    } catch {
      showToast("Failed to fetch events", "error");
    } finally {
      setLoading(false);
    }
  };

  /* ── Derived stats ─────────────────────────────────────── */
  const total    = events.length;
  const selling  = events.filter(e => e.status === "Selling").length;
  const draft    = events.filter(e => e.status === "Draft").length;
  const closing  = events.filter(e => e.status === "Closing").length;

  /* ── Filtered list ─────────────────────────────────────── */
  const filtered = useMemo(() => {
    return events.filter(e => {
      const q = search.toLowerCase();
      const matchSearch = !q || e.name.toLowerCase().includes(q) || (e.slug || "").includes(q);
      const matchFilter =
        filter === "all"     ||
        (filter === "selling" && e.status === "Selling") ||
        (filter === "draft"   && e.status === "Draft")   ||
        (filter === "closing" && e.status === "Closing");
      return matchSearch && matchFilter;
    });
  }, [events, search, filter]);

  /* ── Create Event ──────────────────────────────────────── */
  const handleCreateEvent = () => {
    showModal({
      title: "Create New Event",
      subtitle: "Configure event details and capacity",
      content: (
        <form
          className="space-y-5"
          onSubmit={async (e) => {
            e.preventDefault();
            const fd = new FormData(e.target);
            try {
              await API.post("/events", {
                name:     fd.get("name"),
                date:     fd.get("date"),
                capacity: parseInt(fd.get("capacity")),
                status:   fd.get("status") || "Draft",
              });
              showToast("Event created successfully!", "success");
              fetchEvents();
              closeModal();
            } catch (err) {
              showToast(err.response?.data?.msg || "Failed to create event", "error");
            }
          }}
        >
          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Event Name</label>
            <input name="name" type="text" placeholder="e.g. Annual Summit 2027" className={inputCls} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Date</label>
              <input name="date" type="date" className={inputCls} required />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Capacity</label>
              <input name="capacity" type="number" placeholder="500" min="1" className={inputCls} required />
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Initial Status</label>
            <select name="status" className={inputCls}>
              <option value="Draft">Draft</option>
              <option value="Selling">Selling</option>
            </select>
          </div>
          <button type="submit" className="w-full bg-[#0a0c10] text-white px-8 py-4 rounded-xl text-xs font-black shadow-xl hover:bg-slate-800 uppercase tracking-widest mt-4 transition-all hover:scale-[1.01] active:scale-[0.99]">
            Publish Event
          </button>
        </form>
      ),
    });
  };

  /* ── Edit Event ────────────────────────────────────────── */
  const handleEditEvent = (event) => {
    closeModal();
    showModal({
      title: "Edit Event",
      subtitle: `Modifying · ${event.name}`,
      content: (
        <form
          className="space-y-5"
          onSubmit={async (e) => {
            e.preventDefault();
            const fd = new FormData(e.target);
            try {
              await API.put(`/events/${event._id}`, {
                name:     fd.get("name"),
                date:     fd.get("date"),
                capacity: parseInt(fd.get("capacity")),
                status:   fd.get("status"),
              });
              showToast("Event updated successfully.", "success");
              fetchEvents();
              closeModal();
            } catch (err) {
              showToast(err.response?.data?.msg || "Failed to update event", "error");
            }
          }}
        >
          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Event Name</label>
            <input name="name" type="text" defaultValue={event.name} className={inputCls} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Date</label>
              <input name="date" type="date" defaultValue={event.date} className={inputCls} required />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Capacity</label>
              <input name="capacity" type="number" defaultValue={event.capacity} min="1" className={inputCls} required />
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Status</label>
            <select name="status" defaultValue={event.status} className={inputCls}>
              <option value="Draft">Draft</option>
              <option value="Selling">Selling</option>
              <option value="Closing">Closing</option>
            </select>
          </div>
          <button type="submit" className="w-full bg-[#0a0c10] text-white px-8 py-4 rounded-xl text-xs font-black shadow-xl hover:bg-slate-800 uppercase tracking-widest mt-4 transition-all">
            Save Changes
          </button>
        </form>
      ),
    });
  };

  /* ── Delete Event ──────────────────────────────────────── */
  const handleDeleteEvent = (event) => {
    closeModal();
    showModal({
      title: "Delete Event",
      subtitle: "This action cannot be undone",
      content: (
        <div className="space-y-6">
          <div className="p-5 bg-red-50 border border-red-100 rounded-2xl text-center">
            <p className="text-sm font-black text-slate-800">{event.name}</p>
            <p className="text-[10px] font-bold text-red-500 uppercase tracking-widest mt-1">
              {event.registeredCount || 0} registered attendees will be affected
            </p>
          </div>
          <p className="text-xs text-slate-500 font-bold text-center">Are you sure you want to permanently delete this event?</p>
          <div className="flex gap-3">
            <button
              onClick={closeModal}
              className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-600 text-[10px] font-black uppercase tracking-widest hover:bg-slate-200 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                try {
                  await API.delete(`/events/${event._id}`);
                  showToast("Event deleted successfully.", "success");
                  fetchEvents();
                  closeModal();
                } catch {
                  showToast("Failed to delete event.", "error");
                }
              }}
              className="flex-1 py-3 rounded-xl bg-red-500 text-white text-[10px] font-black uppercase tracking-widest hover:bg-red-600 transition-all"
            >
              Delete Permanently
            </button>
          </div>
        </div>
      ),
    });
  };

  /* ── Event Detail Modal ────────────────────────────────── */
  const handleEventClick = (event) => {
    const registered = event.registeredCount || 0;
    const cap = event.capacity || 1;
    const pct = Math.round((registered / cap) * 100);
    const cfg = statusCfg[event.status] || statusCfg.Draft;

    showModal({
      title: event.name,
      subtitle: `Protocol: ${event._id.slice(-8).toUpperCase()}`,
      content: (
        <motion.div 
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-3"
        >
          {/* Compact Dashboard Hero */}
          <div className="p-5 bg-[#0a0c10] rounded-2xl border border-white/5 relative overflow-hidden">
             <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                   <div className="w-1 h-1 bg-amber-400 rounded-full" />
                   <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest">Live Registry</p>
                </div>
                <span className={`px-2.5 py-1 rounded-full text-[7px] font-black uppercase tracking-widest ${cfg.bg} ${cfg.text}`}>
                   {cfg.label}
                </span>
             </div>

             <div className="grid grid-cols-3 gap-2">
                <div>
                   <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest mb-0.5">Audience</p>
                   <p className="text-3xl font-black text-white tracking-tighter">{registered.toLocaleString()}</p>
                </div>
                <div className="border-l border-white/5 pl-4">
                   <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest mb-0.5">Capacity</p>
                   <p className="text-lg font-black text-white/30 tracking-tighter">{cap.toLocaleString()}</p>
                </div>
                <div className="border-l border-white/5 pl-4">
                   <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest mb-0.5">Fill</p>
                   <p className="text-lg font-black text-amber-400 tracking-tighter">{pct}%</p>
                </div>
             </div>

             <div className="mt-4 h-1 bg-white/5 rounded-full overflow-hidden">
                <motion.div 
                   initial={{ width: 0 }}
                   animate={{ width: `${pct}%` }}
                   transition={{ duration: 1, ease: "easeOut" }}
                   className="h-full bg-amber-400"
                />
             </div>
          </div>

          {/* Core Telemetry & Asset Row */}
          <div className="flex gap-3">
             <div className="flex-1 space-y-3">
                <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100 flex items-center gap-3">
                   <div className="w-7 h-7 rounded-lg bg-white shadow-sm border border-slate-100 flex items-center justify-center text-slate-400">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                   </div>
                   <div>
                      <p className="text-[7px] font-black text-slate-400 uppercase tracking-widest">Date</p>
                      <p className="text-[9px] font-black text-slate-700 uppercase">{new Date(event.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</p>
                   </div>
                </div>
                <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100 flex items-center gap-3">
                   <div className="w-7 h-7 rounded-lg bg-white shadow-sm border border-slate-100 flex items-center justify-center text-slate-400">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                   </div>
                   <div className="min-w-0">
                      <p className="text-[7px] font-black text-slate-400 uppercase tracking-widest">Slug</p>
                      <p className="text-[9px] font-black text-slate-700 uppercase truncate">{event.slug || "AUTO"}</p>
                   </div>
                </div>
             </div>

             <div className="p-4 bg-slate-900 rounded-2xl flex flex-col items-center justify-center gap-2.5 w-[140px] shrink-0 qr-container">
                <div className="bg-white p-1.5 rounded-lg">
                   <QRCodeSVG value={event._id} size={70} level="H" />
                </div>
                <button 
                  onClick={() => {
                    const svg = document.querySelector(".qr-container svg");
                    const svgData = new XMLSerializer().serializeToString(svg);
                    const canvas = document.createElement("canvas");
                    const ctx = canvas.getContext("2d");
                    const img = new Image();
                    img.onload = () => {
                      canvas.width = img.width;
                      canvas.height = img.height;
                      ctx.fillStyle = "white";
                      ctx.fillRect(0, 0, canvas.width, canvas.height);
                      ctx.drawImage(img, 0, 0);
                      const pngFile = canvas.toDataURL("image/png");
                      const downloadLink = document.createElement("a");
                      downloadLink.download = `KEY_${event.name.replace(/\s+/g, '_')}.png`;
                      downloadLink.href = pngFile;
                      downloadLink.click();
                    };
                    img.src = "data:image/svg+xml;base64," + btoa(svgData);
                  }}
                  className="text-[7px] font-black text-amber-500 uppercase tracking-widest hover:text-white transition-colors"
                >
                  Download Key
                </button>
             </div>
          </div>

          {/* Action Row */}
          <div className="flex gap-3 pt-1">
             <button
                onClick={() => handleEditEvent(event)}
                className="flex-1 py-2 rounded-xl bg-slate-100 text-slate-500 text-[12px] font-black uppercase tracking-widest hover:bg-[#0a0c10] hover:text-white transition-all"
             >
                Edit
             </button>
             <button
                onClick={() => handleDeleteEvent(event)}
                className="flex-1 py-2 rounded-xl bg-rose-50 text-rose-500 text-[12px] font-black uppercase tracking-widest hover:bg-rose-500 hover:text-white transition-all"
             >
                Delete
             </button>
          </div>
        </motion.div>
      ),
    });
  };

  /* ── Status cycle on badge click ───────────────────────── */
  const handleStatusClick = async (e, event) => {
    e.stopPropagation();
    const cycle = { Draft: "Selling", Selling: "Closing", Closing: "Draft" };
    const next = cycle[event.status] || "Selling";
    try {
      await API.put(`/events/${event._id}`, { status: next });
      showToast(`Status → ${next}`, "success");
      fetchEvents();
    } catch {
      showToast("Failed to update status.", "error");
    }
  };

  /* ── Render ─────────────────────────────────────────────── */
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={container}
      className="space-y-10"
    >
      {/* Header */}
      <motion.div variants={item} className="flex justify-between items-center px-2">
        <div>
          <h2 className="text-white text-3xl font-black uppercase tracking-tight">Events Management</h2>
          <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mt-2 px-1">
            {total} event{total !== 1 ? "s" : ""} in database
          </p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleCreateEvent}
          className="flex items-center gap-2 bg-white text-[#0a0c10] px-8 py-3.5 rounded-2xl text-xs font-black shadow-2xl uppercase tracking-widest hover:bg-slate-100 transition-all"
        >
          + Create Event
        </motion.button>
      </motion.div>

      {/* Stat cards */}
      <motion.div variants={item} className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { 
            label: "Total Events", 
            value: total,   
            icon: <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 6h16M4 12h16m-7 6h7" /></svg>, 
            accent: "text-white",        
            sub: "all" 
          },
          { 
            label: "Selling",      
            value: selling,  
            icon: <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>, 
            accent: "text-amber-400",    
            sub: "active" 
          },
          { 
            label: "Draft",        
            value: draft,    
            icon: <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>, 
            accent: "text-slate-400",    
            sub: "unpublished" 
          },
          { 
            label: "Closing",      
            value: closing,  
            icon: <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>, 
            accent: "text-red-400",      
            sub: "ended" 
          },
        ].map((s) => (
          <div key={s.label} className="puzzle-card-dark flex flex-col gap-3 relative overflow-hidden group">
            <div className="absolute -top-8 -right-8 w-24 h-24 bg-white/5 rounded-full blur-2xl group-hover:bg-white/10 transition-all duration-700 pointer-events-none" />
            <div className="flex items-center justify-between">
              <span className={`text-xl font-black ${s.accent}`}>{s.icon}</span>
              <span className="text-[9px] font-black uppercase tracking-widest text-slate-600">{s.sub}</span>
            </div>
            <p className={`text-4xl font-black tracking-tighter ${s.accent}`}>
              {loading ? "—" : s.value}
            </p>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">{s.label}</p>
          </div>
        ))}
      </motion.div>

      {/* Table card */}
      <motion.div variants={item} className="puzzle-card-light !p-0 overflow-hidden shadow-2xl border-none">

        {/* Toolbar */}
        <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row gap-3 items-center">
          <div className="relative flex-1 w-full">
            <span className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">⌕</span>
            <input
              type="text"
              placeholder="Search event name or slug…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-3.5 px-5 pl-12 text-sm font-bold text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0a0c10]/10 transition-all"
            />
          </div>
          <div className="flex gap-2">
            {[
              { key: "all",     label: "All" },
              { key: "selling", label: "Selling" },
              { key: "draft",   label: "Draft" },
              { key: "closing", label: "Closing" },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border ${
                  filter === f.key
                    ? "bg-[#0a0c10] text-white border-[#0a0c10] shadow-lg"
                    : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="p-20 text-center text-slate-400 font-bold uppercase tracking-widest animate-pulse">
            Loading events…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-20 flex flex-col items-center justify-center">
            <div className="w-40 h-40 mb-4 opacity-70">
              <Lottie animationData={noDataAnimation} loop />
            </div>
            <p className="text-slate-400 font-bold uppercase tracking-widest text-sm">
              {search || filter !== "all" ? "No events match your filters" : "No events created yet"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-100">
                  <th className="p-6 font-black text-slate-800 uppercase text-[10px] tracking-widest">Event</th>
                  <th className="p-6 font-black text-slate-800 uppercase text-[10px] tracking-widest text-center">Date</th>
                  <th className="p-6 font-black text-slate-800 uppercase text-[10px] tracking-widest text-center">Registrations</th>
                  <th className="p-6 font-black text-slate-800 uppercase text-[10px] tracking-widest text-center">Fill Rate</th>
                  <th className="p-6 font-black text-slate-800 uppercase text-[10px] tracking-widest text-right">Status</th>
                  <th className="p-6 font-black text-slate-800 uppercase text-[10px] tracking-widest text-right">Actions</th>
                </tr>
              </thead>
              <motion.tbody
                initial="hidden"
                animate="visible"
                variants={container}
                className="divide-y divide-slate-50"
              >
                <AnimatePresence>
                  {filtered.map((event, idx) => {
                    const registered = event.registeredCount || 0;
                    const cap        = event.capacity || 1;
                    const pct        = Math.round((registered / cap) * 100);
                    const cfg        = statusCfg[event.status] || statusCfg.Draft;
                    return (
                      <motion.tr
                        variants={row}
                        key={event._id}
                        onClick={() => handleEventClick(event)}
                        className="hover:bg-slate-50/80 transition-all group cursor-pointer"
                      >
                        {/* Event name */}
                        <td className="p-6">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-[#0a0c10] text-white flex items-center justify-center font-black text-lg shadow-md group-hover:scale-110 transition-transform flex-shrink-0">
                              {event.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-black text-slate-800 uppercase tracking-tight">{event.name}</p>
                              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">
                                {event.slug || event._id}
                              </p>
                            </div>
                          </div>
                        </td>

                        {/* Date */}
                        <td className="p-6 text-center text-slate-500 font-black text-xs uppercase tracking-tight">
                          {new Date(event.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                        </td>

                        {/* Registrations */}
                        <td className="p-6 text-center font-black text-slate-800">
                          {registered.toLocaleString()} <span className="text-slate-300">/</span> {cap.toLocaleString()}
                        </td>

                        {/* Fill rate */}
                        <td className="p-6">
                          <div className="flex flex-col items-center gap-2">
                            <span className={`font-black text-sm ${pct > 80 ? "text-red-500" : pct > 50 ? "text-amber-500" : "text-slate-800"}`}>
                              {pct}%
                            </span>
                            <div className="w-28 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <motion.div
                                initial={{ width: 0 }}
                                whileInView={{ width: `${pct}%` }}
                                viewport={{ once: true }}
                                transition={{ duration: 1.2, ease: "easeOut", delay: 0.3 + idx * 0.05 }}
                                className={`h-full rounded-full ${pct > 80 ? "bg-red-400" : pct > 50 ? "bg-amber-400" : "bg-emerald-400"}`}
                              />
                            </div>
                          </div>
                        </td>

                        {/* Status badge — click cycles status */}
                        <td className="p-6 text-right">
                          <span
                            onClick={(e) => handleStatusClick(e, event)}
                            title="Click to cycle status"
                            className={`px-5 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest inline-block cursor-pointer transition-transform hover:scale-105 active:scale-95 ${cfg.bg} ${cfg.text}`}
                          >
                            {cfg.label}
                          </span>
                        </td>

                        {/* Quick actions */}
                        <td className="p-6 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={() => handleEditEvent(event)}
                              className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 text-[10px] font-black uppercase tracking-widest hover:bg-slate-200 transition-all"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteEvent(event)}
                              className="px-3 py-1.5 rounded-lg bg-red-50 text-red-500 text-[10px] font-black uppercase tracking-widest hover:bg-red-100 transition-all"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </motion.tr>
                    );
                  })}
                </AnimatePresence>
              </motion.tbody>
            </table>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}